import {
  EVIDENCE_SORT_COLUMN,
  EVIDENCE_WINDOW_STATUS,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { FlexAlign } from 'src/components/Reusable/Flex'
import { GridColumnSortEvidence } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'

export type EvidenceConfigType = {
  body: Uint8Array | string
  mimeType: string
  isAutomated: boolean
  file: File | null
  name: string
  isDMS?: boolean
}

export enum ReceptorScanType {
  EVIDENCE_RECEPTOR = 'Evidence receptor',
  WORKFLOW_RECEPTOR = 'Workflow receptor',
  DOCUMENT_MANAGEMENT_RECEPTOR = 'Document management receptor',
}

export enum DocumentReceptor {
  GoogleDrive = 'Google Drive',
  SharePoint = 'SharePoint',
  Confluence = 'Confluence',
}

export const EVIDENCE_EXTENSION_TOOLTIP =
  'The evidence extension is a short text appended to the name of any evidence imported from this receptor. It helps identify the source of the evidence, especially when multiple receptors of the same type are configured. For example, extensions such as "production" or "testing" can be used to distinguish evidence collected from different environments.'

export const receptorMap: Record<string, string> = {
  [DocumentReceptor.GoogleDrive]: 'trr-gdrive',
  [DocumentReceptor.SharePoint]: 'trr-sharepoint',
  [DocumentReceptor.Confluence]: 'trr-confluence-dms',
}

export enum EvidenceIndexHeader {
  SELECT = 'select',
  TYPE = 'type',
  NAME = 'name',
  ORIGINAL_FILE = 'original_file',
  LINKED_REQUESTS = 'linked_requests',
  LINKED_CONTROLS = 'linked_controls',
  ADDED_BY = 'added_by',
  RELEVANT_DATE = 'relevant_date',
  LAST_MODIFIED = 'last_modified',
  LAST_CHECKED = 'last_checked',
  CREATED_AT = 'created_at',
  SYNC = 'sync',
  DELETE = 'delete',
  EXPAND = 'expand',
}

export type EvidenceIndexHeaderValue = {
  name: string
  gridAlign: FlexAlign
}

export type EvidenceIndexConfigType = Record<EvidenceIndexHeader, boolean>

export enum EvidenceIndexType {
  READONLY = 'readonly',
  DMS = 'dms',
  LEGACY = 'legacy',
}

export const EVIDENCE_INDEX_CONFIG: Record<
  EvidenceIndexType,
  EvidenceIndexConfigType
> = {
  [EvidenceIndexType.READONLY]: {
    [EvidenceIndexHeader.SELECT]: true,
    [EvidenceIndexHeader.TYPE]: true,
    [EvidenceIndexHeader.NAME]: true,
    [EvidenceIndexHeader.ORIGINAL_FILE]: false,
    // Readonly picker modals (LinkExistingEvidenceModal, individualEvidenceSelector)
    // render EvidenceGroup rows, which have no linked-requests count — never show
    // this column here, regardless of audit context (see useEvidenceIndexHeaders).
    [EvidenceIndexHeader.LINKED_REQUESTS]: false,
    [EvidenceIndexHeader.LINKED_CONTROLS]: true,
    [EvidenceIndexHeader.ADDED_BY]: true,
    [EvidenceIndexHeader.RELEVANT_DATE]: true,
    [EvidenceIndexHeader.LAST_MODIFIED]: false,
    [EvidenceIndexHeader.LAST_CHECKED]: false,
    [EvidenceIndexHeader.CREATED_AT]: true,
    [EvidenceIndexHeader.SYNC]: false,
    [EvidenceIndexHeader.DELETE]: false,
    [EvidenceIndexHeader.EXPAND]: false,
  },
  [EvidenceIndexType.DMS]: {
    [EvidenceIndexHeader.SELECT]: false,
    [EvidenceIndexHeader.TYPE]: true,
    [EvidenceIndexHeader.NAME]: true,
    [EvidenceIndexHeader.ORIGINAL_FILE]: true,
    [EvidenceIndexHeader.LINKED_REQUESTS]: true,
    [EvidenceIndexHeader.LINKED_CONTROLS]: true,
    [EvidenceIndexHeader.ADDED_BY]: true,
    [EvidenceIndexHeader.RELEVANT_DATE]: true,
    [EvidenceIndexHeader.LAST_MODIFIED]: true,
    [EvidenceIndexHeader.LAST_CHECKED]: true,
    [EvidenceIndexHeader.CREATED_AT]: false,
    [EvidenceIndexHeader.SYNC]: true,
    [EvidenceIndexHeader.DELETE]: true,
    [EvidenceIndexHeader.EXPAND]: true,
  },
  [EvidenceIndexType.LEGACY]: {
    [EvidenceIndexHeader.SELECT]: false,
    [EvidenceIndexHeader.TYPE]: true,
    [EvidenceIndexHeader.NAME]: true,
    [EvidenceIndexHeader.ORIGINAL_FILE]: false,
    [EvidenceIndexHeader.LINKED_REQUESTS]: true,
    [EvidenceIndexHeader.LINKED_CONTROLS]: true,
    [EvidenceIndexHeader.ADDED_BY]: true,
    [EvidenceIndexHeader.RELEVANT_DATE]: true,
    [EvidenceIndexHeader.LAST_MODIFIED]: false,
    [EvidenceIndexHeader.LAST_CHECKED]: false,
    [EvidenceIndexHeader.CREATED_AT]: true,
    [EvidenceIndexHeader.SYNC]: false,
    [EvidenceIndexHeader.DELETE]: true,
    [EvidenceIndexHeader.EXPAND]: true,
  },
}

export const EVIDENCE_INDEX_HEADER_TO_HEADER_VALUE: Record<
  EvidenceIndexHeader,
  EvidenceIndexHeaderValue
> = {
  [EvidenceIndexHeader.SELECT]: {
    name: '',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.TYPE]: {
    name: 'Type',
    gridAlign: FlexAlign.FLEX_END,
  },
  [EvidenceIndexHeader.NAME]: {
    name: 'Name',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.LINKED_REQUESTS]: {
    name: 'Linked Requests',
    gridAlign: FlexAlign.CENTER,
  },
  [EvidenceIndexHeader.LINKED_CONTROLS]: {
    name: 'Linked Controls',
    gridAlign: FlexAlign.CENTER,
  },
  [EvidenceIndexHeader.ADDED_BY]: {
    name: 'By',
    gridAlign: FlexAlign.CENTER,
  },
  [EvidenceIndexHeader.ORIGINAL_FILE]: {
    name: 'Source',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.RELEVANT_DATE]: {
    name: 'Relevant Date',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.LAST_MODIFIED]: {
    name: 'Last Modified',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.LAST_CHECKED]: {
    name: 'Last Checked',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.CREATED_AT]: {
    name: 'Created At',
    gridAlign: FlexAlign.FLEX_START,
  },
  [EvidenceIndexHeader.SYNC]: {
    name: '',
    gridAlign: FlexAlign.CENTER,
  },
  [EvidenceIndexHeader.DELETE]: {
    name: '',
    gridAlign: FlexAlign.CENTER,
  },
  [EvidenceIndexHeader.EXPAND]: {
    name: '',
    gridAlign: FlexAlign.CENTER,
  },
}

export const EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS: Record<
  string,
  GridColumnSortEvidence
> = {
  [EvidenceIndexHeader.NAME]: GridColumnSortEvidence.NAME,
  [EvidenceIndexHeader.RELEVANT_DATE]: GridColumnSortEvidence.RELEVANT_DATE,
  [EvidenceIndexHeader.LAST_CHECKED]: GridColumnSortEvidence.LAST_CHECKED,
}

export const EVIDENCE_HEADERS_TO_SORT_COL_WITHOUT_DMS: Record<
  string,
  GridColumnSortEvidence
> = {
  [EvidenceIndexHeader.NAME]: GridColumnSortEvidence.NAME,
  [EvidenceIndexHeader.RELEVANT_DATE]: GridColumnSortEvidence.RELEVANT_DATE,
  [EvidenceIndexHeader.CREATED_AT]: GridColumnSortEvidence.CREATED_AT,
}

export const EVIDENCE_SORT_COLUMNS = Object.freeze({
  [GridColumnSortEvidence.NAME]: EVIDENCE_SORT_COLUMN.NAME,
  [GridColumnSortEvidence.RELEVANT_DATE]: EVIDENCE_SORT_COLUMN.RELEVANT_DATE,
  [GridColumnSortEvidence.CREATED_AT]: EVIDENCE_SORT_COLUMN.CREATED_AT,
  [GridColumnSortEvidence.LAST_CHECKED]: EVIDENCE_SORT_COLUMN.LAST_SCANNED,
})

export const EVIDENCE_INDEX_PAGE_SIZE = 20

// Why a piece of evidence sits outside the window a control test would consider. Keyed by
// EVIDENCE_WINDOW_STATUS; IN_WINDOW has no message because nothing is flagged for it.
export const EVIDENCE_WINDOW_STATUS_TOOLTIPS: Record<number, string> = {
  [EVIDENCE_WINDOW_STATUS.BEFORE_EARLIEST_EVIDENCE_DATE]:
    "This evidence is older than the account's Earliest Evidence Date, so control tests do not consider it.",
  [EVIDENCE_WINDOW_STATUS.BEFORE_AUDIT_START]:
    'This evidence is older than the audit start date, so control tests do not consider it.',
  [EVIDENCE_WINDOW_STATUS.AFTER_AUDIT_END]:
    'This evidence is newer than the audit end date, so control tests do not consider it.',
  [EVIDENCE_WINDOW_STATUS.FUTURE_RELEVANT_DATE]:
    'This evidence has a relevant date in the future, so control tests do not consider it.',
}

// Query param that reveals out-of-window evidence. A param rather than component state so
// the choice survives a refresh and can be linked to, like the page's other filters.
export const SHOW_ALL_DATES_PARAM = 'showAllDates'

// Empty-state copy for when the window hid everything. Without this the reader is told
// they have added no evidence while a count of hidden evidence sits directly above.
export const ALL_EVIDENCE_OUT_OF_WINDOW_TITLE =
  'No evidence in the current date range'
export const ALL_EVIDENCE_OUT_OF_WINDOW_NOTE =
  'Every piece of evidence here falls outside the date range control tests consider. Choose Show All above to see it.'

export const OUT_OF_WINDOW_SHOW_ALL_LABEL = 'Show All'
export const OUT_OF_WINDOW_HIDE_LABEL = 'Hide'

// Marks a version the window excludes. The show page lists every version — the reader
// navigated to this evidence, and a version list that quietly skips versions is worse than
// one that explains them — so this labels rather than hides.
export const OUT_OF_WINDOW_VERSION_SUFFIX = ' (out of range)'

// The show page's badge for the version on screen. It replaced a count of every
// out-of-window evidence in the account, which said nothing about the page it sat on.
export const OUT_OF_WINDOW_PILL_LABEL = 'Out of range'
